import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useToolbar } from '@ohif/core';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icons,
} from '@ohif/ui-next';
import {
  countToolsThatFit,
  estimateAvailableToolbarWidth,
  estimateToolWidth,
} from './countToolsThatFit';

const MOBILE_TOOLBAR_MQ = '(max-width: 767px)';

type ToolbarHookState = ReturnType<typeof useToolbar>;

type ToolEntry = {
  key: string;
  toolDef: NonNullable<ToolbarHookState['toolbarButtons'][number]>;
  toolbarState: ToolbarHookState;
};

function useMobileToolbarLayout(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(MOBILE_TOOLBAR_MQ).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_TOOLBAR_MQ);
    const onChange = () => setIsMobile(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

function renderToolItem(
  entry: ToolEntry,
  viewportId?: string,
  options?: { overflow?: boolean; onAfterInteraction?: () => void }
) {
  const { toolDef, toolbarState } = entry;
  const { id, Component, componentProps } = toolDef;
  const { onInteraction, isItemOpen, isItemLocked, openItem, closeItem, toggleLock } =
    toolbarState;

  const enhancedProps = {
    ...componentProps,
    isOpen: isItemOpen(id, viewportId),
    isLocked: isItemLocked(id, viewportId),
    onOpen: () => openItem(id, viewportId),
    onClose: () => closeItem(id, viewportId),
    onToggleLock: () => toggleLock(id, viewportId),
    viewportId,
  };

  return (
    <div
      key={entry.key}
      className={options?.overflow ? 'viewer-toolbar-overflow-cell' : 'flex shrink-0 items-center'}
      data-toolbar-slot={options?.overflow ? 'overflow' : 'primary'}
    >
      <Component
        id={id}
        onInteraction={args => {
          onInteraction({
            ...args,
            itemId: id,
            viewportId,
          });
          options?.onAfterInteraction?.();
        }}
        {...enhancedProps}
      />
    </div>
  );
}

function ToolbarOverflowMenu({ tools }: { tools: ToolEntry[] }) {
  const [open, setOpen] = useState(false);

  if (!tools.length) {
    return null;
  }

  const gridTools = tools.filter(entry => entry.toolDef.id !== 'Layout');
  const layoutTool = tools.find(entry => entry.toolDef.id === 'Layout');

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="viewer-toolbar-more-btn"
          aria-label="More tools"
          title="More tools"
        >
          <Icons.More
            className="h-5 w-5"
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={8}
        className="viewer-chrome-light viewer-toolbar-overflow-menu z-[100] border border-[#e5e5e5] bg-white p-0 shadow-lg"
      >
        <div className="viewer-toolbar-overflow-grid">
          {gridTools.map(entry =>
            renderToolItem(entry, undefined, {
              overflow: true,
              onAfterInteraction: () => setOpen(false),
            })
          )}
        </div>
        {layoutTool ? (
          <div className="viewer-toolbar-overflow-layout">
            {renderToolItem(layoutTool, undefined, {
              overflow: true,
            })}
          </div>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type ViewerResponsiveToolbarProps = {
  patientMode: boolean;
  leftToolSections: { key: string; section: string }[];
  actions: React.ReactNode;
};

export default function ViewerResponsiveToolbar({
  patientMode,
  leftToolSections,
  actions,
}: ViewerResponsiveToolbarProps) {
  const isMobile = useMobileToolbarLayout();
  const primaryRef = useRef<HTMLDivElement>(null);
  const [measuredCount, setMeasuredCount] = useState<number | null>(null);

  const measurementTools = useToolbar({ buttonSection: 'measurementTools' });
  const navigationTools = useToolbar({ buttonSection: 'navigationTools' });
  const layoutTools = useToolbar({ buttonSection: 'layoutTools' });
  const viewManipulationTools = useToolbar({ buttonSection: 'viewManipulationTools' });
  const probeDisplayTools = useToolbar({ buttonSection: 'probeDisplayTools' });
  const secondaryTools = useToolbar({ buttonSection: 'secondary' });

  const sectionStateByName = useMemo(
    () => ({
      measurementTools,
      navigationTools,
      layoutTools,
      viewManipulationTools,
      probeDisplayTools,
      secondary: secondaryTools,
    }),
    [
      measurementTools,
      navigationTools,
      layoutTools,
      viewManipulationTools,
      probeDisplayTools,
      secondaryTools,
    ]
  );

  const primaryToolEntries = useMemo(() => {
    const entries: ToolEntry[] = [];

    leftToolSections.forEach(({ section }) => {
      const toolbarState = sectionStateByName[section as keyof typeof sectionStateByName];
      if (!toolbarState) {
        return;
      }

      toolbarState.toolbarButtons.forEach(toolDef => {
        if (!toolDef) {
          return;
        }
        entries.push({
          key: `${section}-${toolDef.id}`,
          toolDef,
          toolbarState,
        });
      });
    });

    return entries;
  }, [leftToolSections, sectionStateByName]);

  const secondaryToolEntries = useMemo(
    () =>
      secondaryTools.toolbarButtons
        .filter(Boolean)
        .map(toolDef => ({
          key: `secondary-${toolDef!.id}`,
          toolDef: toolDef!,
          toolbarState: secondaryTools,
        })),
    [secondaryTools]
  );

  const restPrimaryEntries = useMemo(
    () => primaryToolEntries.filter(entry => entry.toolDef.id !== 'Layout'),
    [primaryToolEntries]
  );
  const layoutEntry = useMemo(
    () => primaryToolEntries.find(entry => entry.toolDef.id === 'Layout') ?? null,
    [primaryToolEntries]
  );

  const estimatedCount = useMemo(() => {
    const widths = restPrimaryEntries.map(entry => estimateToolWidth(entry.toolDef.id));
    const reservedLayout = layoutEntry ? estimateToolWidth('Layout') + 4 : 0;
    return countToolsThatFit(
      Math.max(0, estimateAvailableToolbarWidth() - reservedLayout),
      widths
    );
  }, [restPrimaryEntries, layoutEntry]);

  const shownCount = Math.min(measuredCount ?? estimatedCount, restPrimaryEntries.length);

  useLayoutEffect(() => {
    const host = primaryRef.current;
    if (!host) {
      return;
    }

    const updateFit = () => {
      const restNodes = Array.from(
        host.querySelectorAll<HTMLElement>('[data-toolbar-slot="primary"]')
      );
      const layoutWidth = layoutEntry
        ? (restNodes[restNodes.length - 1]?.offsetWidth || estimateToolWidth('Layout'))
        : 0;
      const restMeasured = layoutEntry ? restNodes.slice(0, -1) : restNodes;
      const widths = restPrimaryEntries.map((entry, index) => {
        if (restMeasured[index]?.offsetWidth > 0) {
          return restMeasured[index].offsetWidth;
        }
        return estimateToolWidth(entry.toolDef.id);
      });
      const next = countToolsThatFit(
        Math.max(0, host.clientWidth - (layoutEntry ? layoutWidth + 4 : 0)),
        widths
      );
      setMeasuredCount(prev => (prev === next ? prev : next));
    };

    updateFit();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(updateFit);
    observer.observe(host);
    return () => observer.disconnect();
  }, [restPrimaryEntries, layoutEntry, shownCount]);

  const visiblePrimaryTools = [
    ...restPrimaryEntries.slice(0, shownCount),
    ...(layoutEntry ? [layoutEntry] : []),
  ];
  const overflowPrimaryTools = restPrimaryEntries.slice(shownCount);
  const overflowEntries = isMobile
    ? [...overflowPrimaryTools, ...secondaryToolEntries]
    : overflowPrimaryTools;

  return (
    <div
      className={`viewer-ris-toolbar flex h-full min-h-0 w-full items-center justify-between gap-2 ${
        isMobile ? 'viewer-ris-toolbar--mobile' : ''
      }`}
    >
      <div
        ref={primaryRef}
        className="viewer-ris-toolbar__primary relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
      >
        {visiblePrimaryTools.map(entry => renderToolItem(entry))}
        <ToolbarOverflowMenu tools={overflowEntries} />
      </div>
      <div className="viewer-ris-toolbar__actions flex max-w-[33%] shrink-0 items-center justify-end gap-1">
        {!patientMode && !isMobile
          ? secondaryToolEntries.map(entry => renderToolItem(entry))
          : null}
        {actions}
      </div>
    </div>
  );
}
